import {
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { CompanyStateService } from 'src/app/auth/services/company-state.service';
import { TemplateEditorService } from 'src/app/template-editor/services/template-editor.service';
import { environment } from 'src/environments/environment';
import { CapAlertDisplayData } from '../../../types/cap-alerts-page.types';

/** A change to the alert data reaches the template at most this often. */
const ATTRIBUTE_DATA_DEBOUNCE_MS = 300;

/**
 * Shows a template as a display renders it, or a placeholder for a custom
 * presentation. It loads the template's own page in preview mode and sends it
 * the same three messages the editor preview does; it listens to none.
 */
@Component({
  selector: 'cap-alert-preview',
  templateUrl: './cap-alert-preview.component.html',
  styleUrl: './cap-alert-preview.component.scss',
  imports: [MatIconModule],
})
export class CapAlertPreviewComponent {
  private readonly sanitizer = inject(DomSanitizer);
  private readonly companyStateService = inject(CompanyStateService);
  private readonly destroyRef = inject(DestroyRef);

  /** The template to render; empty for a custom presentation. */
  readonly productCode = input('');
  /** JSON: the `attributeData` message's value. */
  readonly attributeData = input('');
  readonly displayData = input<CapAlertDisplayData | null>(null);
  /** The text alternative naming what is previewed. */
  readonly label = input('');
  readonly presentationName = input('');
  /** Width over height of the template's own canvas. */
  readonly aspectRatio = input(16 / 9);
  /** The template's own canvas width in px; 0 until its blueprint is known. */
  readonly contentWidth = input(0);

  private readonly box = viewChild<ElementRef<HTMLElement>>('box');
  private readonly iframe = viewChild<ElementRef<HTMLIFrameElement>>('frame');

  private readonly boxWidth = signal(0);
  private iframeLoaded = false;
  private attributeTimer: ReturnType<typeof setTimeout> | undefined;

  protected readonly isPresentation = computed(
    () => !this.productCode() && !!this.presentationName(),
  );

  /** A template is shown once its canvas size is known; until then (or if its blueprint failed) a placeholder. */
  protected readonly showsTemplate = computed(
    () => !!this.productCode() && this.contentWidth() > 0,
  );

  protected readonly contentHeight = computed(
    () => this.contentWidth() / this.aspectRatio(),
  );

  protected readonly scale = computed(() =>
    this.contentWidth() > 0 ? this.boxWidth() / this.contentWidth() : 0,
  );

  protected readonly templateUrl = computed<SafeResourceUrl | null>(() => {
    const productCode = this.productCode();
    if (!productCode) {
      return null;
    }

    // Built only from a catalog product code and the company id.
    const url =
      environment.HTML_TEMPLATE_URL.replace(
        'PRODUCT_CODE',
        encodeURIComponent(productCode),
      ) +
      '?type=preview&cid=' +
      encodeURIComponent(this.companyStateService.getSelectedCompanyId());
    return this.sanitizer.bypassSecurityTrustResourceUrl(url);
  });

  constructor() {
    // A new template, or a new iframe, is a new document: nothing can be
    // posted until it loads.
    effect(() => {
      this.productCode();
      this.showsTemplate();
      untracked(() => {
        this.iframeLoaded = false;
        this.clearAttributeTimer();
      });
    });

    effect(() => {
      this.attributeData();
      untracked(() => this.scheduleAttributeData());
    });

    // The box is only there for a template, so observe it whenever it appears.
    effect((onCleanup) => {
      const element = this.box()?.nativeElement;
      if (!element || typeof ResizeObserver === 'undefined') {
        return;
      }

      const observer = new ResizeObserver((entries) => {
        const width = entries[entries.length - 1]?.contentRect.width;
        if (width !== undefined) {
          this.boxWidth.set(width);
        }
      });
      observer.observe(element);
      onCleanup(() => observer.disconnect());
    });

    this.destroyRef.onDestroy(() => this.clearAttributeTimer());
  }

  protected onLoad(): void {
    this.iframeLoaded = true;
    this.clearAttributeTimer();

    const displayData = this.displayData();
    if (displayData) {
      this.post({ type: 'displayData', value: displayData });
    }
    this.postAttributeData();
    this.post({ type: 'sendStartEvent' });
  }

  private scheduleAttributeData(): void {
    this.clearAttributeTimer();
    if (!this.iframeLoaded) {
      return;
    }
    this.attributeTimer = setTimeout(() => {
      this.attributeTimer = undefined;
      this.postAttributeData();
    }, ATTRIBUTE_DATA_DEBOUNCE_MS);
  }

  private postAttributeData(): void {
    const attributeData = this.attributeData();
    if (attributeData) {
      this.post({ type: 'attributeData', value: JSON.parse(attributeData) });
    }
  }

  private clearAttributeTimer(): void {
    if (this.attributeTimer !== undefined) {
      clearTimeout(this.attributeTimer);
      this.attributeTimer = undefined;
    }
  }

  private post(message: { type: string; value?: unknown }): void {
    if (!this.iframeLoaded) {
      return;
    }
    this.iframe()?.nativeElement.contentWindow?.postMessage(
      JSON.stringify(message),
      TemplateEditorService.HTML_TEMPLATE_DOMAIN,
    );
  }
}
